/**
 * Version Management Hooks
 *
 * React Query hooks for lesson version operations:
 * - List versions with content status
 * - Activate (switch to) a specific version
 *
 * Pending-source detection moved to the client: the staged diff is computed
 * in `StagedActionBar` from `lesson.staged_source_ids` vs the current
 * version's `source_ids`. Editing the prompt is a draft (no version) via
 * `lessonApi.editPrompt`; the bottom-bar "Create new version" button is
 * what actually fires `useRegenerateLesson`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { versionApi } from '@/lib/api-client';
import type {
  VersionListResponse,
  VersionContentResponse,
  ActivateVersionResponse,
} from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';

// Query key factory
export const versionKeys = {
  all: ['versions'] as const,
  list: (lessonId: string) => [...versionKeys.all, 'list', lessonId] as const,
  content: (lessonId: string, version: string) =>
    [...versionKeys.all, 'content', lessonId, version] as const,
};

/**
 * List all versions for a lesson
 */
export function useVersionList(lessonId: string | undefined) {
  return useQuery<VersionListResponse>({
    queryKey: versionKeys.list(lessonId ?? ''),
    queryFn: () => versionApi.listVersions(lessonId!),
    enabled: !!lessonId,
    staleTime: 30_000,
  });
}

/**
 * Get content for a specific version
 */
export function useVersionContent(
  lessonId: string | undefined,
  version: string | undefined
) {
  return useQuery<VersionContentResponse>({
    queryKey: versionKeys.content(lessonId ?? '', version ?? ''),
    queryFn: () => versionApi.getVersionContent(lessonId!, version!),
    enabled: !!lessonId && !!version,
    staleTime: 60_000,
  });
}

/**
 * Switch to a specific version (rollback)
 */
export function useActivateVersion(lessonId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<ActivateVersionResponse, Error, string>({
    mutationFn: (version: string) =>
      versionApi.activateVersion(lessonId!, version),
    onSuccess: () => {
      // Invalidate the version list so the active flag flips locally.
      queryClient.invalidateQueries({ queryKey: versionKeys.list(lessonId!) });

      // Invalidate ALL `['lessons', ...]` queries - covers the
      // MyLearningPage list (so the card's title/description/version
      // tag refreshes without a hard reload), per-lesson metadata,
      // detail polls, and pipeline status. Previously this hook used
      // legacy keys `['lesson', ...]` (singular) that didn't actually
      // match anything in `queryKeys.lessons` (plural), so the
      // learning card stayed stale until the user refreshed.
      queryClient.invalidateQueries({ queryKey: queryKeys.lessons.all });

      // Aggregated pipeline-status poll uses its own key (separate
      // from the `lessons.*` namespace).
      queryClient.invalidateQueries({
        queryKey: queryKeys.lessons.pipelineStatus(lessonId ?? ''),
      });

      // Per-artifact GETs registered under the on-demand keys (text,
      // mindmap, video, highlights, quiz, glossary, extras) - flip them
      // all so the new version's content shows up in the panels.
      queryClient.invalidateQueries({ queryKey: ['on-demand'] });
    },
  });
}
